import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cn } from '@/utils/cn';
import styles from './ProgressBar.module.scss';
import type { ProgressBarProps } from './ProgressBar.types';

/**
 * ProgressBar - Visual progress indicator
 * Based on Radix UI Progress with SCSS styling
 *
 * @example
 * <ProgressBar value={60} showLabel />
 * <ProgressBar value={30} variant="warning" size="lg" />
 */
export const ProgressBar = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressBarProps
>(
  (
    {
      value,
      max = 100,
      size = 'md',
      variant = 'primary',
      showLabel = false,
      className,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const percentage = Math.round((value / max) * 100);

    const progressClasses = cn(
      styles.progressBar,
      styles[`progressBar--${size}`],
      className
    );

    const indicatorClasses = cn(
      styles.progressBar__indicator,
      styles[`progressBar__indicator--${variant}`]
    );

    return (
      <div className={styles.progressBar__wrapper}>
        <ProgressPrimitive.Root
          ref={ref}
          className={progressClasses}
          value={value}
          max={max}
          aria-label={ariaLabel || `Progress: ${percentage}%`}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={indicatorClasses}
            style={{ transform: `translateX(-${100 - percentage}%)` }}
          />
        </ProgressPrimitive.Root>

        {showLabel && (
          <span className={styles.progressBar__label} aria-live="polite">
            {percentage}%
          </span>
        )}
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';
